import { action, atom } from '@reatom/framework'
import { io } from 'socket.io-client'

import { LOG_WS_EMITS_ENABLED } from '@root/shared/environment'

import type { Ctx } from '@reatom/framework'
import type { Socket } from 'socket.io-client'

export type EventHandlerWithCtxMap<T extends DefaultEventsMap> = {
  [K in keyof T]: (ctx: Ctx, ...args: Parameters<T[K]>) => void
}

// Picked from socket.io-client/build/esm/socket.d.ts
interface DefaultEventsMap {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [event: string]: (...args: Array<any>) => void
}

type SocketState = 'connected' | 'disconnected' | 'connecting'

export const reatomSocket = <
  ListenEventsMap extends DefaultEventsMap,
  EmitEventsMap extends DefaultEventsMap,
>({
  namespace,
  listenEventsMap,
  host,
  onForcefullyReconnect,
}: {
  namespace?: string
  host: string
  listenEventsMap: ListenEventsMap
  /**
   * Callback to be called when the socket is forcefully connected after server .disconnect
   */
  onForcefullyReconnect?: (ctx: Ctx) => void
}) => {
  const socketStateAtom = atom<SocketState>('disconnected', 'socketStateAtom')

  const socketAtom = atom<Socket<ListenEventsMap, EmitEventsMap> | undefined>(
    undefined,
    'socketAtom',
  )
  const initializeSocketAction = action((ctx) => {
    const existingSocket = ctx.get(socketAtom)
    if (existingSocket) {
      return existingSocket
    }

    const socket = io(`${host}${namespace ?? ''}`, {
      path: '/ws',
      transports: ['websocket'],
    }) as Socket<ListenEventsMap, EmitEventsMap>

    for (const [event, handler] of Object.entries(listenEventsMap)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const handlerWithCtx: any = (...args: Array<any>) => handler(ctx, ...args)
      socket.on(event, handlerWithCtx)
    }

    socket.on('disconnect', (reason) => {
      socketStateAtom(ctx, 'disconnected')
      // eslint-disable-next-line no-console
      console.log('[WebSocket] Disconnect', reason)
      if (reason === 'io server disconnect') {
        socket.connect()
        socket.once('connect', () => {
          onForcefullyReconnect?.(ctx)
        })
        return
      }
    })

    socket.on('connect', () => {
      socketStateAtom(ctx, 'connected')
    })
    socket.on('connect_error', () => {
      socketStateAtom(ctx, 'connecting')
    })
    socket.io.on('open', () => {
      socketStateAtom(ctx, 'connecting')
    })

    socketAtom(ctx, socket)
    return socket
  }, 'initializeSocketAction')

  const emitAction = action((ctx, event: string, ...args: Array<object>) => {
    let socket = ctx.get(socketAtom)
    if (!socket) {
      socket = initializeSocketAction(ctx)
    }

    if (LOG_WS_EMITS_ENABLED && event !== 'latency:ping') {
      // eslint-disable-next-line no-console
      console.log(
        `[WebSocket] [${namespace}] emitAction`,
        event,
        JSON.stringify(args),
      )
    }
    socket.emit(event, ...(args as Parameters<EmitEventsMap[string]>))
  }, 'emitAction')

  return {
    socketAtom,
    emitAction,
    initializeSocketAction,
  }
}
